import { apiError } from "../../../../../../utils/formErrors";
import React, { useState, useEffect } from "react";
import { Box, Stack, Dialog, Checkbox } from '@mui/material';
import DataTable from "../../../../../../component/DataTable";
import MainBox from "../../../../container/MainBox";
import TopBar from "../../../../../../component/TopBar";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import FormField from "../../../../../../component/Formfield";
import { StatusChip, headCellSx, bodyCellSx } from "../../../../../../component/listing";
import { FieldLabel } from "../../../../../../component/wizard";
import { getSalaryTemplates, createSalaryTemplate, updateSalaryTemplate, getSalaryComponents } from "../../../../../../services/payroll";
import { getActiveLegalUnitId } from "../../../../../../services/globalData";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";

const calcLabel = (c) => c.calculationType === "PERCENTAGE" ? `${c.value || 0}% of ${c.percentageOf || "CTC"}` : `₹ ${Number(c.value || 0).toLocaleString("en-IN")}/yr`;

const SalaryTemplates = () => {
  const notify = useNotify();
  const legalUnit = getActiveLegalUnitId();
  const [templates, setTemplates] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialog, setDialog] = useState(null); // { editing, _id, name, selected:Set }
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const [t, c] = await Promise.all([getSalaryTemplates(legalUnit), getSalaryComponents(legalUnit)]);
      if (t?.success === false) throw new Error(t?.message || "Failed to load templates");
      setTemplates((Array.isArray(t?.data) ? t.data : []).filter((x) => !x.isDeleted));
      setComponents((Array.isArray(c?.data) ? c.data : []).filter((x) => !x.isDeleted));
    } catch (e) {
      setError(true);
      notify.error(e?.message || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const openNew = () => setDialog({ editing: false, name: "", selected: new Set() });
  const openEdit = (t) => setDialog({ editing: true, _id: t._id, name: t.name, selected: new Set((t.linkedComponent || []).map((c) => c._id || c)) });
  const toggle = (id) => setDialog((d) => {
    const s = new Set(d.selected); s.has(id) ? s.delete(id) : s.add(id);
    return { ...d, selected: s };
  });

  const save = async () => {
    if (!dialog.name.trim()) { notify.error("Template name is required"); return; }
    const linkedComponent = [...dialog.selected];
    if (linkedComponent.length === 0) { notify.error("Pick at least one component"); return; }
    setSaving(true);
    try {
      const res = dialog.editing
        ? await updateSalaryTemplate(dialog._id, { name: dialog.name, linkedComponent })
        : await createSalaryTemplate({ name: dialog.name, linkedComponent, legalUnit, isActive: true });
      if (res?.success === false) throw apiError(res, "Could not save template");
      notify.success(dialog.editing ? "Template updated" : "Template created");
      setDialog(null);
      fetchData();
    } catch (e) {
      notify.error(e?.message || "Could not save template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Payroll"} breadcrumbs={[{ label: "Salary Components", href: "/app/settings/payroll/components" }, { label: "Templates" }]} />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Salary Templates</Box>
          <Stack direction="row" spacing={1}>
            <BtnSecondary to="/app/settings/payroll/components" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
            <BtnPrimary onClick={openNew} sx={{ borderRadius: "8px" }}>+ New Template</BtnPrimary>
          </Stack>
        </Stack>

        <DataTable
          rows={templates}
          loading={loading}
          error={error}
          onRetry={fetchData}
          errorTitle="Couldn't load templates"
          empty={{
            title: "No templates",
            description: "Create a reusable template of components to apply when assigning salary structures.",
          }}
          onRowClick={(t) => openEdit(t)}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={[
            { key: "name", label: "Template", width: "30%", cellSx: { fontWeight: 600 }, render: (t) => t.name },
            {
              key: "components",
              label: "Components",
              cellSx: { color: tokens.textMuted },
              render: (t) => (t.linkedComponent || []).map((c) => c.name || "—").join(", ") || "—",
            },
            {
              key: "status",
              label: "Status",
              render: (t) => <StatusChip label={t.isActive ? "Active" : "Inactive"} tone={t.isActive ? "success" : "muted"} />,
            },
          ]}
        />
      </MainBox>

      <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} PaperProps={{ sx: { borderRadius: "12px", p: "1.5rem", width: "34rem", maxWidth: "94vw" } }}>
        {dialog && (
          <>
            <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text, mb: "1.25rem" }}>{dialog.editing ? "Edit template" : "New template"}</Box>
            <Box sx={{ mb: "1.25rem" }}>
              <FieldLabel required>Template name</FieldLabel>
              <FormField type="text" value={dialog.name} onChange={(e) => setDialog((d) => ({ ...d, name: e.target.value }))} fullwidth />
            </Box>
            <FieldLabel>Components</FieldLabel>
            <Box sx={{ maxHeight: "16rem", overflowY: "auto", border: `1px solid ${tokens.border}`, borderRadius: "10px", mt: "0.5rem" }}>
              {components.length === 0 ? (
                <Box sx={{ p: "1rem", fontSize: "0.8125rem", color: tokens.textMuted }}>No components in the catalog yet.</Box>
              ) : components.map((c) => (
                <Stack key={c._id} direction="row" alignItems="center" onClick={() => toggle(c._id)}
                  sx={{ px: "0.75rem", py: "0.5rem", cursor: "pointer", borderBottom: `1px solid ${tokens.border}`, "&:hover": { background: tokens.surfaceSubtle } }}>
                  <Checkbox checked={dialog.selected.has(c._id)} size="small" sx={{ p: 0, mr: 1 }} />
                  <Box sx={{ flex: 1, fontSize: "0.875rem", fontWeight: 500 }}>{c.name}</Box>
                  <StatusChip label={(c.component || "").toLowerCase()} tone={c.component === "DEDUCTION" ? "danger" : c.component === "REIMBURSEMENT" ? "info" : "success"} />
                  <Box sx={{ ml: 1.5, fontSize: "0.8125rem", color: tokens.textMuted, minWidth: "7rem", textAlign: "right" }}>{calcLabel(c)}</Box>
                </Stack>
              ))}
            </Box>
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: "2rem" }}>
              <BtnSecondary onClick={() => setDialog(null)} disabled={saving} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
              <BtnPrimary onClick={save} disabled={saving} sx={{ borderRadius: "8px" }}>{saving ? "Saving…" : dialog.editing ? "Save" : "Create"}</BtnPrimary>
            </Stack>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default SalaryTemplates;
